import React, { useState, useEffect } from 'react';
import './MachineGroupManager.css';
import { api, fetchMachines, updateMachineStatus } from '../services/sensorService';

const MachineGroupManager = () => {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [groups, setGroups] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // 머신 목록 로드
  useEffect(() => {
    loadMachines();
  }, []);

  // 선택된 머신의 그룹/센서 로드
  useEffect(() => {
    if (selectedMachine?.machine_id) {
      loadMachineData(selectedMachine.machine_id);
    }
  }, [selectedMachine?.machine_id]);
  

  const loadMachines = async () => {
    try {
      const data = await fetchMachines();
      setMachines(data);
      if (data.length > 0 && !selectedMachine) {
        setSelectedMachine(data[0]);
      }
    } catch (error) {
      console.error('설비 목록 로드 오류:', error);
    }
  };

  const loadMachineData = async (machineId) => {
    setDataLoading(true);
    try {
      const [machineRes, groupsRes, sensorsRes] = await Promise.all([
        api.get('/machines'),
        api.get(`/machine-groups/${machineId}`),
        api.get(`/sensors/${machineId}`)
      ]);

      const groupsData = groupsRes.data || [];
      const sensorsData = sensorsRes.data || [];

      // ✅ 그룹이 하나도 없으면: 새 그룹 자동 생성 + 모든 센서를 그 그룹으로 이동(복사 X, 이동)
      if (groupsData.length === 0) {
        //const newGroupId = `tmp_${Date.now()}`;
        const newGroupId = -Date.now(); // 음수 정수 임시 ID
        const defaultGroup = {
          group_id: newGroupId,
          group_name: '새 그룹 1',
          machine_id: machineId,
          memo: '',
          isNew: true
        };

        const assignedSensors = sensorsData.map(s => ({
          ...s,
          group_id: newGroupId
        }));

        setGroups([defaultGroup]);
        setSensors(assignedSensors);

        setSelectedMachine(prev =>
          prev && prev.machine_id === machineId
            ? { ...prev, current_group: newGroupId }
            : prev
        );
      } else {
        setGroups(groupsData);
        setSensors(sensorsData);
        // 머신의 current_group 정보를 사용하여 설정
        const machineData = machineRes.data.find(m => m.machine_id === machineId);
        const currentGroup = machineData?.current_group || groupsData[0]?.group_id || null;
        setSelectedMachine(prev =>
          prev && prev.machine_id === machineId
            ? { ...prev, current_group: currentGroup }
            : prev
        );
      }

      console.log('로드된 데이터:', {
        groups: groupsData.length,
        sensors: sensorsData.length
      });
    } catch (error) {
      console.error('머신 데이터 로드 실패:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleGroupChange = (groupId, field, value) => {
    setGroups(prev =>
      prev.map(group =>
        String(group.group_id) === String(groupId)
          ? { ...group, [field]: value }
          : group
      )
    );
  };

  const handleDefaultGroupChange = (groupId) => {
    setSelectedMachine(prev =>
      prev ? { ...prev, current_group: groupId } : prev
    );
  };

  const handleSensorChange = (sensorId, field, value) => {
    setSensors(prev =>
      prev.map(sensor =>
        sensor.sensor_id === sensorId
          ? { ...sensor, [field]: value }
          : sensor
      )
    );
  };

  // ✅ null로 내려오면 기본 그룹(현재 기본 또는 첫 그룹)으로 강제 배정
  const handleSensorGroupChange = (sensorId, groupId) => {
    let target = groupId;
    if (target == null) {
      const fallback =
        selectedMachine?.current_group ??
        (groups.length > 0 ? groups[0].group_id : null);

      if (fallback == null) {
        console.warn('배정할 그룹이 없습니다.');
        return;
      }
      target = fallback;
    }

    setSensors(prev =>
      prev.map(sensor =>
        sensor.sensor_id === sensorId
          ? { ...sensor, group_id: target }
          : sensor
      )
    );
  };

  const handleDragStart = (e, sensor) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(sensor));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetGroupId) => {
    e.preventDefault();
    try {
      const sensorData = JSON.parse(e.dataTransfer.getData('text/plain'));
      handleSensorGroupChange(sensorData.sensor_id, targetGroupId);
    } catch (error) {
      console.error('드래그 앤 드롭 오류:', error);
    }
  };

  const addGroup = () => {
    if (!selectedMachine) return;
  
    const newGroupId = -Date.now(); //임시 음수 정수 ID
    const newGroup = {
      group_id: newGroupId,
      group_name: `새 그룹 ${groups.length + 1}`,
      machine_id: selectedMachine.machine_id,
      memo: '',
      isNew: true
    };
  
    setGroups(prev => [...prev, newGroup]);
  
    // 기본 그룹(체크된 그룹)의 센서들을 복사하여 새 그룹에 추가
    const currentGroupId = selectedMachine.current_group;
    const currentGroupSensors = sensors.filter(s => Number(s.group_id) === Number(currentGroupId));
    
    const copiedSensors = currentGroupSensors.map(sensor => ({
      ...sensor,
      sensor_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // 임시 센서 ID
      group_id: newGroupId,
      isNew: true
    }));
  
    setSensors(prev => [...prev, ...copiedSensors]);
  
    // 체크박스는 기존 그룹에 유지 (새 그룹으로 이동하지 않음)
    // setSelectedMachine(prev => prev ? { ...prev, current_group: newGroupId } : prev);
  };
  

  // 그룹 X 클릭 시 그룹과 하위 센서 삭제
  const removeGroup = (groupId) => {
    setGroups(prev => prev.filter(g => String(g.group_id) !== String(groupId)));
    setSensors(prev => prev.filter(s => String(s.group_id) !== String(groupId)));
    setSelectedMachine(prev =>
      prev
        ? {
            ...prev,
            current_group: String(prev.current_group) === String(groupId)
              ? null
              : prev.current_group
          }
        : prev
    );
  };

  const checkDuplicateLimits = () => {
    const groupLimits = new Map();
    const duplicates = [];

    // 각 그룹별로 모든 센서의 상한/하한값을 수집
    groups.forEach(group => {
      const groupSensors = sensors.filter(s => Number(s.group_id) === Number(group.group_id));
      if (groupSensors.length > 0) {
        // 그룹 내 모든 센서의 상한/하한값을 정렬하여 키 생성
        const uplimits = groupSensors.map(s => s.uplimit_value).sort().join(',');
        const downlimits = groupSensors.map(s => s.downlimit_value).sort().join(',');
        const key = `${uplimits}_${downlimits}`;
        
        if (groupLimits.has(key)) {
          const existingGroup = groupLimits.get(key);
          duplicates.push({
            group1: existingGroup.group_name,
            group2: group.group_name,
            uplimits: uplimits,
            downlimits: downlimits
          });
        } else {
          groupLimits.set(key, group);
        }
      }
    });

    return duplicates;
  };

  const saveChanges = async () => {
    if (!selectedMachine) return;

    // 중복된 상한/하한값 확인
    const duplicates = checkDuplicateLimits();
    if (duplicates.length > 0) {
      const duplicateInfo = duplicates.map(dup => 
        `- ${dup.group1}과 ${dup.group2}: 상한값 [${dup.uplimits}], 하한값 [${dup.downlimits}]`
      ).join('\n');
      
      alert(`동일한 상한/하한값을 가진 그룹이 있습니다:\n\n${duplicateInfo}`);
      return;
    }

    setSaveLoading(true);
    try {
      // current_group을 실제 그룹 ID로 매핑
      let mappedCurrentGroup = selectedMachine.current_group;
      if (groups.length > 0) {
        // 임시 ID인 경우 첫 번째 그룹의 실제 ID 사용
        if (mappedCurrentGroup < 0 || mappedCurrentGroup == null) {
          mappedCurrentGroup = groups[0].group_id;
        }
      }

      const { data: result } = await api.post('/machine-groups/save', {
        machine_id: selectedMachine.machine_id,
        current_group: mappedCurrentGroup,
        groups,
        sensors
      });

      console.log('저장 결과:', result);

      // 머신 목록도 다시 로드하여 current_group 정보 업데이트
      await loadMachines();
      await loadMachineData(selectedMachine.machine_id);
      alert(`저장되었습니다. (그룹: ${result.group_count}, 센서: ${result.sensor_count})`);
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setSaveLoading(false);
    }
  };

  const getSensorsByGroup = (groupId) => sensors.filter(s => Number(s.group_id) === Number(groupId));

  // 설비 상태 변경
  const handleMachineStatusToggle = async (machine) => {
    try {
      // is_active가 null이나 undefined인 경우 true(가동중)로 처리
      const currentStatus = Boolean(machine.is_active ?? true);
      const newStatus = !currentStatus;
      
      await updateMachineStatus(machine.machine_id, newStatus);
      
      // 로컬 상태 업데이트
      setMachines(prev => 
        prev.map(m => 
          m.machine_id === machine.machine_id 
            ? { ...m, is_active: newStatus }
            : m
        )
      );
      
      // 선택된 설비 상태도 업데이트
      if (selectedMachine?.machine_id === machine.machine_id) {
        setSelectedMachine(prev => ({ ...prev, is_active: newStatus }));
      }
      
      alert(`설비가 ${newStatus ? '가동' : '비가동'} 상태로 변경되었습니다.`);
    } catch (error) {
      console.error('설비 상태 변경 오류:', error);
      alert('설비 상태 변경에 실패했습니다.');
    }
  };

  // 머신 이름을 표시용 이름으로 변환
  const getDisplayName = (machineName) => {
    const nameMap = {
      'A-BAN-1': 'A동 열처리',
      'A-BAN-4': '4-2호기',
      'C-BAN-8': '4-1호기'
    };
    return nameMap[machineName] || machineName;
  };

  return (
    <div className="machine-group-manager">
      {/* 좌측: 설비 목록 */}
      <div className="left-panel">
        <div className="panel-header">
          <h3>설비 목록</h3>
        </div>
        <div className="machine-list">
          {machines.map(machine => (
            <div
              key={machine.machine_id}
              className={`machine-item ${selectedMachine?.machine_id === machine.machine_id ? 'selected' : ''} ${!Boolean(machine.is_active ?? true) ? 'inactive' : ''}`}
            >
              <div 
                className="machine-info"
                onClick={() => setSelectedMachine(machine)}
              >
                <span className="machine-icon">🏭</span>
                <span className="machine-name">
                  {getDisplayName(machine.machine_name)}
                </span>
              </div>
              <button
                className={`status-toggle-btn ${Boolean(machine.is_active ?? 1) ? 'active' : 'inactive'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMachineStatusToggle(machine);
                }}
                title={`${Boolean(machine.is_active ?? 1) ? '비가동' : '가동'} 상태로 변경`}
              >
                {Boolean(machine.is_active ?? 1) ? '가동' : '비가동'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 우측: 그룹 편집 */}
      <div className="right-panel">
        <div className="panel-header">
          <h3>그룹 편집</h3>
          <div className="header-buttons">
            <button onClick={addGroup} disabled={!selectedMachine || saveLoading || dataLoading}>
              그룹 추가
            </button>
            <button onClick={saveChanges} disabled={!selectedMachine || saveLoading}>
              {saveLoading ? '저장중...' : '저장'}
            </button>
          </div>
        </div>

        {selectedMachine ? (
          <div className="group-editor">
            <div className="sensor-grid">
              <div className="grid-header">
                <div className="col-name">그룹 / 계측기</div>
                <div className="col-setting">설정값</div>
                <div className="col-limit">상한</div>
                <div className="col-limit">하한</div>
              </div>

              {/* ✅ ‘그룹없음’ 섹션 제거 */}

              {/* 그룹별 센서들 */}
              {groups.map(group => (
                <React.Fragment key={group.group_id}>
                  <div
                    className="grid-row group-row drop-zone"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, group.group_id)}
                  >
                    <div className="col-name">
                      <input
                        type="checkbox"
                        checked={Number(selectedMachine?.current_group) === Number(group.group_id)}
                        onChange={() => handleDefaultGroupChange(group.group_id)}
                        className="default-checkbox"
                        title="기본 그룹 설정"
                      />
                      <span className="group-icon">📁</span>
                      <input
                        type="text"
                        value={group.group_name}
                        onChange={(e) => handleGroupChange(group.group_id, 'group_name', e.target.value)}
                        className="group-name-input"
                      />
                      <button
                        onClick={() => removeGroup(group.group_id)}
                        className="inline-remove-btn"
                        title="그룹 제거"
                      >
                        ✖
                      </button>
                    </div>
                    <div className="col-setting">-</div>
                    <div className="col-limit">-</div>
                    <div className="col-limit">-</div>
                  </div>

                  {/* 그룹에 속한 센서들 */}
                  {getSensorsByGroup(group.group_id).length > 0 ? (
                    getSensorsByGroup(group.group_id).map(sensor => (
                      <div
                        key={sensor.sensor_id}
                        className="grid-row sensor-row grouped draggable"
                        draggable
                        onDragStart={(e) => handleDragStart(e, sensor)}
                      >
                        <div className="col-name">
                          <span className="indent">└</span>
                          <span className="drag-handle">☰</span>
                          <span className="sensor-icon">📊</span>
                          {sensor.sensor_name}
                        </div>
                        <div className="col-setting">
                          <input
                            type="number"
                            value={sensor.setting_value ?? 0}
                            readOnly
                            className="value-input readonly"
                            placeholder="설정값"
                          />
                        </div>
                        <div className="col-limit">
                          <input
                            type="number"
                            value={sensor.uplimit_value ?? ''}
                            onChange={(e) => handleSensorChange(sensor.sensor_id, 'uplimit_value', e.target.value === '' ? null : parseFloat(e.target.value))}
                            onFocus={(e) => e.target.select()}
                            className="value-input"
                            placeholder="상한"
                          />
                        </div>
                        <div className="col-limit">
                          <input
                            type="number"
                            value={sensor.downlimit_value ?? ''}
                            onChange={(e) => handleSensorChange(sensor.sensor_id, 'downlimit_value', e.target.value === '' ? null : parseFloat(e.target.value))}
                            onFocus={(e) => e.target.select()}
                            className="value-input"
                            placeholder="하한"
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="grid-row empty-group">
                      <div className="col-name">
                        <span className="indent">└</span>
                        <span className="empty-message">센서를 드래그해서 추가하세요</span>
                      </div>
                      <div className="col-setting">-</div>
                      <div className="col-limit">-</div>
                      <div className="col-limit">-</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : (
          <div className="no-selection">
            좌측에서 설비를 선택하세요.
          </div>
        )}
      </div>

      {dataLoading && <div className="loading-overlay">불러오는 중...</div>}
    </div>
  );
};

export default MachineGroupManager;
